import React, { useEffect, useRef } from 'react';
import { ArrowUpRight, ArrowDown } from 'lucide-react';
import { BRAND, WHATSAPP_LINK } from '../data/mock';
import MagneticButton from './MagneticButton';
import Counter from './Counter';

const STATS = [
  { type: 'counter', to: 50, prefix: '+', suffix: '', label: 'Marcas atendidas' },
  { type: 'counter', to: 4.9, decimals: 1, label: 'Avaliação média' },
  { type: 'static', value: '<1h', label: 'Tempo de resposta' },
  { type: 'static', value: '0%', label: 'Fidelidade obrigatória' },
];

export default function Hero() {
  const heroRef = useRef(null);
  const orbRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (!heroRef.current || !orbRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      orbRef.current.style.transform = `translate3d(${cx * 0.06}px, ${cy * 0.06}px, 0) rotate(${cx * 0.02}deg)`;
    };
    const el = heroRef.current;
    if (el) el.addEventListener('mousemove', onMove);
    return () => { if (el) el.removeEventListener('mousemove', onMove); };
  }, []);

  return (
    <section id="top" ref={heroRef} className="relative pt-[120px] pb-16 md:pt-[160px] md:pb-24 overflow-hidden grain">
      {/* Decorative orb */}
      <div ref={orbRef} className="pointer-events-none absolute top-1/2 right-[-8%] w-[620px] h-[620px] -translate-y-1/2 transition-transform duration-200 ease-out">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#C8FF61]/20 via-[#16E0C9]/10 to-transparent blur-3xl" />
        <div className="absolute inset-[15%] rounded-full border border-[#C8FF61]/20 spin-slow" />
        <div className="absolute inset-[28%] rounded-full border border-[#16E0C9]/30 spin-slow" style={{ animationDuration: '40s', animationDirection: 'reverse' }} />
        <div className="absolute inset-[42%] rounded-full bg-gradient-to-br from-[#C8FF61]/40 to-[#16E0C9]/30 blur-2xl float-y" />
      </div>

      <div className="thesis-container relative z-10">
        {/* Top meta line */}
        <div className="flex items-center justify-between border-b border-white/8 pb-5">
          <span className="font-mono-label text-[#8E8B85]">
            (01) — Estúdio de Marketing Digital
          </span>
          <span className="font-mono-label text-[#8E8B85] hidden sm:block">
            EST. {BRAND.founded} · {BRAND.coords}
          </span>
          <span className="font-mono-label text-[#C8FF61] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C8FF61] animate-pulse" />
            Aceitando 3 vagas
          </span>
        </div>

        {/* Headline */}
        <div className="pt-12 md:pt-20">
          <h1 className="font-display-tight text-[#F5F1E8] text-[52px] sm:text-[80px] md:text-[128px] lg:text-[180px] leading-[0.86] reveal">
            Marketing
          </h1>
          <h1 className="font-display-tight text-[#F5F1E8] text-[52px] sm:text-[80px] md:text-[128px] lg:text-[180px] leading-[0.86] flex flex-wrap items-end gap-x-4 md:gap-x-6 reveal reveal-d-1">
            <span className="font-serif-italic kinetic-grad">
              que
            </span>
            <span>cabe</span>
          </h1>
          <h1 className="font-display-tight text-[#F5F1E8] text-[52px] sm:text-[80px] md:text-[128px] lg:text-[180px] leading-[0.86] flex items-baseline gap-4 md:gap-6 reveal reveal-d-2">
            <span>no bolso.</span>
            <span className="hidden md:inline-block w-[140px] h-[3px] bg-[#C8FF61] mb-7" />
          </h1>

          {/* Sub-grid */}
          <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end">
            <div className="md:col-span-5 reveal reveal-d-3">
              <p className="text-[18px] md:text-[20px] leading-[1.5] text-[#F5F1E8]/85 max-w-[460px]">
                <span className="font-serif-italic text-[#C8FF61]">15 posts profissionais</span> por apenas
                <strong className="font-medium text-[#F5F1E8]"> R$ 300/mês.</strong> Sites que vendem a partir de
                <strong className="font-medium text-[#F5F1E8]"> R$ 600.</strong> Sem fidelidade. Sem letra miúda.
              </p>
            </div>

            <div className="md:col-span-4 md:col-start-7 flex flex-col gap-3 reveal reveal-d-3">
              <span className="font-mono-label text-[#8E8B85]">(02) — Pronto pra começar?</span>
              <div className="flex flex-wrap gap-3">
                <MagneticButton
                  href={WHATSAPP_LINK('Olá! Vim pelo site da Thesis e quero falar sobre o plano Essencial.')}
                  target="_blank" rel="noopener noreferrer" className="cursor-hover"
                >
                  <span className="btn-primary pulse-ring">
                    Falar no WhatsApp
                    <ArrowUpRight size={18} strokeWidth={2.5} />
                  </span>
                </MagneticButton>
                <a href="#precos" className="btn-secondary cursor-hover">Ver preços</a>
              </div>
            </div>

            <div className="md:col-span-3 md:col-start-11 hidden md:flex flex-col gap-2 items-end text-right reveal reveal-d-4">
              <span className="font-mono-label text-[#8E8B85]">A partir de</span>
              <span className="font-display-tight text-[80px] leading-none kinetic-grad">R$300</span>
              <span className="font-mono-label text-[#8E8B85]">/mês · plano essencial</span>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/8 reveal">
          {STATS.map((s, i) => (
            <div key={i} className="bg-[#0A0B0E] py-7 px-5 group cursor-hover">
              <div className="font-display-tight text-[44px] md:text-[56px] leading-none text-[#F5F1E8] group-hover:text-[#C8FF61] transition-colors duration-500">
                {s.type === 'counter' ? (
                  <Counter to={s.to} prefix={s.prefix || ''} suffix={s.suffix || ''} decimals={s.decimals || 0} />
                ) : (
                  s.value
                )}
              </div>
              <div className="font-mono-label text-[#8E8B85] mt-3">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll cue */}
        <div className="mt-12 flex items-center justify-between border-t border-white/8 pt-5">
          <span className="font-mono-label text-[#8E8B85] flex items-center gap-2">
            <ArrowDown size={14} className="animate-bounce" /> role pra continuar
          </span>
          <span className="font-mono-label text-[#8E8B85] hidden sm:block">
            (s/n) {String(new Date().getFullYear()).slice(2)}–∞
          </span>
        </div>
      </div>
    </section>
  );
}
